import { z } from 'zod';

export const AccountRoleSchema = z.enum(['learner', 'teacher', 'admin']);
export type AccountRole = z.infer<typeof AccountRoleSchema>;
export const AccountStatusSchema = z.enum(['active', 'inactive']);
export const AccountEmailSchema = z.string().trim().toLowerCase().email().max(254);
export const AccountPasswordSchema = z.string().min(12, 'Use at least 12 characters.').max(128).refine(value => value.trim().length >= 12, 'Use at least 12 characters excluding surrounding spaces.');
export const PublicUserSchema = z.object({
  id: z.string().min(1), name: z.string(), email: z.string(), role: AccountRoleSchema,
  status: AccountStatusSchema, mustChangePassword: z.boolean(),
  expiresAt: z.string().datetime().nullable(), createdAt: z.string().datetime(),
});
export type PublicUser = z.infer<typeof PublicUserSchema>;
export type CanNTalkPrincipal = PublicUser & { kind: 'account' | 'reviewer' | 'bootstrap' };
export const AccountClassSchema = z.object({
  id: z.string().uuid(), name: z.string(), description: z.string(),
  teacherIds: z.array(z.string().uuid()), studentIds: z.array(z.string().uuid()),
  archived: z.boolean(), createdAt: z.string().datetime(), updatedAt: z.string().datetime(),
});
export type AccountClass = z.infer<typeof AccountClassSchema>;
export const ConsentReceiptSchema = z.object({ version: z.string(), acceptedAt: z.string().datetime() });
export type ConsentReceipt = z.infer<typeof ConsentReceiptSchema>;
export const AuthIdentitySchema = z.object({
  user: PublicUserSchema, kind: z.enum(['account', 'reviewer', 'bootstrap']), consent: ConsentReceiptSchema.nullable(),
});
export type AuthIdentity = z.infer<typeof AuthIdentitySchema>;
export const CreateAccountSchema = z.object({
  name: z.string().trim().min(1).max(100), email: AccountEmailSchema, role: AccountRoleSchema,
  password: AccountPasswordSchema, expiresAt: z.string().datetime().nullable().optional(),
}).strict();
export const UpdateAccountSchema = CreateAccountSchema.omit({ password: true }).partial().extend({ status: AccountStatusSchema.optional() }).strict();
export const CreateClassSchema = z.object({
  name: z.string().trim().min(1).max(120), description: z.string().trim().max(1000).default(''),
  teacherIds: z.array(z.string().uuid()).max(100).default([]), studentIds: z.array(z.string().uuid()).max(1000).default([]),
}).strict();
export const UpdateClassSchema = CreateClassSchema.partial().extend({ archived: z.boolean().optional() }).strict();
export const TeachingClassesSchema = z.object({ classes: z.array(AccountClassSchema), students: z.array(PublicUserSchema) });
export type TeachingClasses = z.infer<typeof TeachingClassesSchema>;
